import React, { useEffect, useMemo, useState } from "react";
import "../create-exam.css";

/* ======= إعدادات عامة ======= */
const API_BASE = "https://hassen.sstli.com/api/index.php";
const STUDENTS_API = "https://498c26acc261.ngrok-free.app/api/StudentStudyInfo/by-branch/47fa287e-2517-4424-b58f-bd67f9a042d9";

async function apiPost(action, payload) {
  const res = await fetch(`${API_BASE}?action=${encodeURIComponent(action)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    mode: "cors",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/* مكوّن إدخال عام */
function Field({ label, children, required = false, hint }) {
  return (
    <div className="field">
      <label className="field-label">
        {label} {required && <span className="req">*</span>}
      </label>
      {children}
      {hint ? <div className="hint">{hint}</div> : null}
    </div>
  );
}

/* شارة صغيرة */
function Badge({ children, tone = "neutral" }) {
  return <span className={`badge ${tone}`}>{children}</span>;
}

/* مكون السؤال */
function QuestionItem({ question, index, onEdit, onDelete }) {
  return (
    <div className={`question-item ${question.type === 'TF' ? 'tf-type' : 'mcq-type'}`}>
      <div className="question-content">
        <div className="question-header">
          <div className="question-number">السؤال {index + 1}</div>
          <div className="question-actions">
            <button className="btn small" onClick={() => onEdit(question)}>تعديل</button>
            <button className="btn small danger" onClick={() => onDelete(question.id)}>حذف</button>
          </div>
        </div>

        <div className="question-text">{question.text}</div>

        <div className="question-options">
          {question.type === "TF" ? (
            <div className="tf-options">
              <div className={`option ${question.options?.[0]?.is_correct || question.options?.[0]?.correct ? 'correct' : ''}`}>
                <span className="option-marker">أ)</span> صح
              </div>
              <div className={`option ${question.options?.[1]?.is_correct || question.options?.[1]?.correct ? 'correct' : ''}`}>
                <span className="option-marker">ب)</span> خطأ
              </div>
            </div>
          ) : (
            <div className="mcq-options">
              {(question.options || []).map((opt, i) => (
                <div key={i} className={`option ${(opt.is_correct || opt.correct) ? 'correct' : ''}`}>
                  <span className="option-marker">{String.fromCharCode(1570 + i)})</span> {opt.opt_text || opt.text}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ترتيب مستويات عربية */
const LEVEL_ORDER = ["الأول","الثاني","الثالث","الرابع","الخامس","السادس"];
const levelRank = (name) => {
  const i = LEVEL_ORDER.indexOf(name?.trim() || "");
  return i === -1 ? 999 : i;
};

/* ======= الصفحة الرئيسية ======= */
export default function CreateExam() {
  const [metaLoading, setMetaLoading] = useState(false);
  const [metaErr, setMetaErr] = useState("");
  const [studentsData, setStudentsData] = useState([]);

  const [form, setForm] = useState({
    diploma_id: "",
    level_id: "",
    course_id: "",
    title: "",
    total_grade: 60,
    duration_minutes: 60,
  });

  const [examId, setExamId] = useState(null);
  const [examSaved, setExamSaved] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [sectionOrder, setSectionOrder] = useState(["TF", "MCQ"]);

  // وضع التعديل
  const [editingId, setEditingId] = useState(null);
  const [qType, setQType] = useState("TF");
  const [qText, setQText] = useState("");
  const [score, setScore] = useState(1);
  const [tfCorrect, setTfCorrect] = useState("صح");
  const [mcqOpts, setMcqOpts] = useState([
    { id: 1, text: "", correct: false },
    { id: 2, text: "", correct: false },
  ]);

  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");

  /* تحميل الدبلومات/المستويات */
useEffect(() => {
  let cancelled = false;
  (async () => {
    setMetaLoading(true);
    setMetaErr("");
    try {
      const res = await fetch(STUDENTS_API, {
        mode: "cors",
        headers: {
          'ngrok-skip-browser-warning': 'true',
          'Content-Type': 'application/json'
        }
      });
      const json = await res.json();
      if (!cancelled) {
        const arr = Array.isArray(json) ? json : [];
        setStudentsData(arr);
      }
    } catch (e) {
      if (!cancelled) {
        setMetaErr("تعذر تحميل الدبلومات/المستويات من الـ API. تم الاعتماد على بيانات تجريبية.");
        const fake = [
          { diplomName: "دبلوم القانون", levelName: "الأول" },
          { diplomName: "دبلوم الموارد البشرية", levelName: "الرابع" },
          { diplomName: "دبلوم إدارة الأعمال", levelName: "الرابع" },
          { diplomName: "دبلوم إدارة الأعمال", levelName: "الأول" },
          { diplomName: "دبلوم الأمن السيبراني", levelName: "الأول" },
          { diplomName: "دبلوم إدارة الأعمال", levelName: "السادس" },
        ];
        setStudentsData(fake);
      }
    } finally {
      if (!cancelled) setMetaLoading(false);
    }
  })();
  return () => { cancelled = true; };
}, []);

  const diplomas = useMemo(() => {
    const set = new Set(studentsData.map(s => (s.diplomName || "").trim()).filter(Boolean));
    return Array.from(set).sort();
  }, [studentsData]);

  const levels = useMemo(() => {
    const filtered = studentsData.filter(s =>
      !form.diploma_id
        ? true
        : (s.diplomName || "").trim() === (form.diploma_id || "").trim()
    );
    const set = new Set(filtered.map(s => (s.levelName || "").trim()).filter(Boolean));
    return Array.from(set).sort((a,b) => levelRank(a) - levelRank(b));
  }, [studentsData, form.diploma_id]);

  const courses = useMemo(() => ([
    { id: "CLD", name: "مقرر الحوسبة السحابية", diploma: "دبلوم الأمن السيبراني", levels: LEVEL_ORDER },
    { id: "CYB", name: "مقرر الجرائم الإلكترونية", diploma: "دبلوم الأمن السيبراني", levels: LEVEL_ORDER },
  ]), []);

  const filteredCourses = useMemo(() => {
    return courses.filter(c => {
      if (form.diploma_id && c.diploma !== form.diploma_id) return false;
      if (form.level_id && !c.levels.includes(form.level_id)) return false;
      return true;
    });
  }, [courses, form.diploma_id, form.level_id]);

  /* تحميل الامتحان تلقائيًا عند اكتمال الاختيارات */
  useEffect(() => {
    const canLoad = form.diploma_id && form.level_id && form.course_id;
    if (!canLoad) return;

    (async () => {
      try {
        setBusy(true);
        const res = await apiPost("get_exam_by_meta", {
          diploma_name: form.diploma_id,
          level_name: form.level_id,
          course_code: form.course_id,
        });
        if (res.ok && res.exam) {
          setExamId(res.exam.id);
          setExamSaved(false);
          setForm(f => ({
            ...f,
            title: res.exam.title || f.title,
            total_grade: res.exam.total_grade ?? f.total_grade,
            duration_minutes: res.exam.duration_minutes ?? f.duration_minutes,
          }));
          // map الأسئلة والخيارات لخانة الواجهة
          const mapped = (res.questions || []).map(q => ({
            id: q.id,
            type: q.type,
            text: q.text,
            score: Number(q.score || 1),
            options: (q.options || []).map(o => ({
              id: o.id,
              text: o.opt_text,
              correct: !!o.is_correct
            }))
          }));
          setQuestions(mapped);
          setToast("تم تحميل الامتحان الموجود، يمكنك التعديل.");
        } else {
          // لا يوجد امتحان — نفرّغ
          setExamId(null);
          setQuestions([]);
        }
      } catch (e) {
        setToast("تعذر جلب الامتحان (سيرفر).");
      } finally {
        setBusy(false);
      }
    })();
  }, [form.diploma_id, form.level_id, form.course_id]);

  /* إنشاء امتحان جديد إن لم يوجد */
  const createExam = async () => {
    if (!form.diploma_id || !form.level_id || !form.course_id || !form.title) {
      setToast("أكمل الحقول المطلوبة.");
      return;
    }
    setBusy(true);
    setToast("");
    try {
      const payload = {
        diploma_name: form.diploma_id,
        level_name: form.level_id,
        course_code: form.course_id,
        title: form.title.trim(),
        total_grade: Number(form.total_grade) || 60,
        duration_minutes: Number(form.duration_minutes) || 60,
      };
      const res = await apiPost("create_exam", payload);
      if (res.ok) {
        setExamId(res.exam_id);
        setExamSaved(false);
        setToast("تم إنشاء الامتحان. يمكنك الآن إضافة/تعديل الأسئلة.");
      } else {
        throw new Error(res.error || "فشل إنشاء الامتحان");
      }
    } catch (e) {
      const fakeId = Math.floor(Math.random() * 1e7);
      setExamId(fakeId);
      setExamSaved(false);
      setToast("تم إنشاء الامتحان (وضع تجريبي).");
    } finally {
      setBusy(false);
    }
  };

  /* حفظ نهائي */
  const saveExam = async () => {
    if (questions.length === 0) {
      setToast("أضف أسئلة أولاً قبل الحفظ.");
      return;
    }
    if (!examId) {
      setToast("لا يوجد امتحان محفوظ بعد.");
      return;
    }
    setBusy(true);
    setToast("");
    try {
      await apiPost("finalize_exam", { exam_id: examId });
      setExamSaved(true);
      setToast("تم حفظ الامتحان بنجاح!");
    } catch (e) {
      setExamSaved(true);
      setToast("تم حفظ الامتحان بنجاح! (وضع تجريبي)");
    } finally {
      setBusy(false);
    }
  };

  /* إدارة خيارات MCQ */
  const updateOpt = (id, patch) => {
    setMcqOpts(prev => prev.map(o => (o.id === id ? { ...o, ...patch } : o)));
  };
  const addOpt = () => {
    setMcqOpts(prev => [...prev, { id: (prev.at(-1)?.id || 0) + 1, text: "", correct: false }]);
  };
  const removeOpt = (id) => {
    setMcqOpts(prev => prev.filter(o => o.id !== id));
  };
  const setCorrectOnly = (id) => {
    setMcqOpts(prev => prev.map(o => ({ ...o, correct: o.id === id })));
  };

  /* بدء تعديل سؤال */
  const startEdit = (q) => {
    setEditingId(q.id);
    setQType(q.type);
    setQText(q.text);
    setScore(Number(q.score || 1));
    if (q.type === "TF") {
      const isTrueCorrect = !!q.options?.[0]?.correct;
      setTfCorrect(isTrueCorrect ? "صح" : "خطأ");
      setMcqOpts([
        { id: 1, text: "صح", correct: isTrueCorrect },
        { id: 2, text: "خطأ", correct: !isTrueCorrect },
      ]);
    } else {
      const mapped = (q.options || []).map((o, i) => ({
        id: i + 1,
        text: o.text,
        correct: !!o.correct
      }));
      setMcqOpts(mapped.length > 0 ? mapped : [
        { id: 1, text: "", correct: false },
        { id: 2, text: "", correct: false },
      ]);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /* حذف سؤال */
  const deleteQuestion = async (qid) => {
    if (!qid) return;
    if (!window.confirm("تأكيد حذف السؤال؟")) return;
    try {
      setBusy(true);
      const res = await apiPost("delete_question", { question_id: qid });
      if (res.ok) {
        setQuestions(prev => prev.filter(q => q.id !== qid));
        setToast("تم حذف السؤال.");
      } else {
        setToast("فشل حذف السؤال.");
      }
    } catch {
      setToast("تعذر حذف السؤال (سيرفر).");
    } finally {
      setBusy(false);
    }
  };

  /* إضافة/تحديث سؤال */
  const submitQuestion = async () => {
    if (!examId) { setToast("أنشئ الامتحان أولًا."); return; }
    if (!qText.trim()) { setToast("اكتب نص السؤال."); return; }

    if (editingId) {
      // تحديث سؤال
      const payload = {
        question_id: editingId,
        type: qType,
        text: qText.trim(),
        score: Number(score) || 1,
        options: qType === "TF"
          ? [
              { text: "صح",  correct: tfCorrect === "صح"  },
              { text: "خطأ", correct: tfCorrect === "خطأ" },
            ]
          : mcqOpts.filter(o => o.text.trim()).map(o => ({
              text: o.text.trim(), correct: !!o.correct
            })),
      };

      if (qType === "MCQ") {
        const nonEmpty = mcqOpts.filter(o => o.text.trim());
        if (nonEmpty.length < 2) { setToast("أضف خيارين على الأقل."); return; }
        if (!nonEmpty.some(o => o.correct)) { setToast("حدد الإجابة الصحيحة."); return; }
      }

      try {
        setBusy(true); setToast("");
        const res = await apiPost("save_question_full", payload);
        if (res.ok) {
          // حدّث الحالة محليًا
          setQuestions(prev => prev.map(q => {
            if (q.id !== editingId) return q;
            return {
              ...q,
              type: payload.type,
              text: payload.text,
              score: payload.score,
              options: payload.options.map((o, i) => ({ id: i+1, text: o.text, correct: !!o.correct })),
            };
          }));
          // نظف مود التعديل
          setEditingId(null);
          setQText("");
          setScore(1);
          setQType("TF");
          setTfCorrect("صح");
          setMcqOpts([{ id: 1, text: "", correct: false }, { id: 2, text: "", correct: false }]);
          setToast("تم تحديث السؤال.");
        } else {
          setToast("فشل تحديث السؤال.");
        }
      } catch (e) {
        setToast("تعذر تحديث السؤال (سيرفر).");
      } finally {
        setBusy(false);
      }
      return;
    }

    // إضافة جديد (نفس لوجيكك القديم)
    if (qType === "MCQ") {
      const nonEmpty = mcqOpts.filter(o => o.text.trim());
      if (nonEmpty.length < 2) { setToast("أضف خيارين على الأقل."); return; }
      if (!nonEmpty.some(o => o.correct)) { setToast("حدد الإجابة الصحيحة."); return; }
    }

    try {
      setBusy(true); setToast("");
      const addQRes = await apiPost("add_question", {
        exam_id: Number(examId),
        type: qType,
        text: qText.trim(),
        score: Number(score) || 1,
        order_no: questions.length + 1,
      });

      let qid = addQRes.ok ? addQRes.question_id : null;

      if (qType === "TF") {
        const tfOptions = [
          { text: "صح", correct: tfCorrect === "صح" },
          { text: "خطأ", correct: tfCorrect === "خطأ" },
        ];
        if (qid) {
          for (const opt of tfOptions) {
            await apiPost("add_option", {
              question_id: Number(qid),
              opt_text: opt.text,
              is_correct: opt.correct ? 1 : 0,
            });
          }
        }
        setQuestions(prev => [...prev, {
          id: qid || Date.now(),
          type: "TF",
          text: qText.trim(),
          score: Number(score) || 1,
          options: tfOptions
        }]);
      } else {
        const cleaned = mcqOpts.filter(o => o.text.trim()).map(o => ({
          text: o.text.trim(),
          correct: !!o.correct
        }));
        if (qid) {
          for (const opt of cleaned) {
            await apiPost("add_option", {
              question_id: Number(qid),
              opt_text: opt.text,
              is_correct: opt.correct ? 1 : 0,
            });
          }
        }
        setQuestions(prev => [...prev, {
          id: qid || Date.now(),
          type: "MCQ",
          text: qText.trim(),
          score: Number(score) || 1,
          options: cleaned
        }]);
      }

      setQText("");
      setScore(1);
      setTfCorrect("صح");
      setQType("TF");
      setMcqOpts([{ id: 1, text: "", correct: false }, { id: 2, text: "", correct: false }]);
      setToast("تمت إضافة السؤال.");
    } catch (e) {
      setToast("تمت إضافة السؤال (وضع تجريبي).");
      const local = qType === "TF"
        ? [{ text: "صح", correct: tfCorrect === "صح" }, { text: "خطأ", correct: tfCorrect === "خطأ" }]
        : mcqOpts.filter(o => o.text.trim()).map(o => ({ text: o.text.trim(), correct: !!o.correct }));

      setQuestions(prev => [...prev, {
        id: Date.now(),
        type: qType,
        text: qText.trim(),
        score: Number(score) || 1,
        options: local
      }]);
      setQText(""); setScore(1); setTfCorrect("صح"); setQType("TF");
      setMcqOpts([{ id: 1, text: "", correct: false }, { id: 2, text: "", correct: false }]);
    } finally {
      setBusy(false);
    }
  };

  /* فصل الأسئلة حسب النوع */
  const tfQuestions = questions.filter(q => q.type === "TF");
  const mcqQuestions = questions.filter(q => q.type === "MCQ");

  const fisherYates = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  const reorderQuestions = (orderType) => {
    const tf = questions.filter(q => q.type === "TF");
    const mcq = questions.filter(q => q.type === "MCQ");

    switch(orderType) {
      case 'tfFirst': {
        setSectionOrder(["TF", "MCQ"]);
        setQuestions([...tf, ...mcq]);
        break;
      }
      case 'mcqFirst': {
        setSectionOrder(["MCQ", "TF"]);
        setQuestions([...mcq, ...tf]);
        break;
      }
      case 'shuffle': {
        const tfShuffled = fisherYates(tf);
        const mcqShuffled = fisherYates(mcq);
        const [first, second] = sectionOrder;
        const firstList  = first === "TF" ? tfShuffled : mcqShuffled;
        const secondList = second === "TF" ? tfShuffled : mcqShuffled;
        setQuestions([...firstList, ...secondList]);
        break;
      }
      default: break;
    }
  };

  const canCreate = !!form.diploma_id && !!form.level_id && !!form.course_id && !!form.title?.trim() && !examId;

  return (
    <div className="create-exam page">
      <div className="page-head">
        <div>
          <h1>إضافة اختبار</h1>
          <p className="sub">اختَر الدبلوم والمستوى والمقرر. لو الامتحان موجود هيتجاب تلقائيًا ويظهر للتعديل.</p>
        </div>
        <div className="exam-status">
          {examId && <Badge tone={examSaved ? "green" : "blue"}>
            {examSaved ? "تم الحفظ" : `Exam ID: ${examId}`}
          </Badge>}
          {!examId && <Badge>جديد</Badge>}
        </div>
      </div>

      {/* ميتاداتا */}
      <div className="card">
        <div className="grid g3">
          <Field label="الدبلوم" required>
            <select
              value={form.diploma_id}
              onChange={(e) => {
                const v = e.target.value;
                setForm(f => ({ ...f, diploma_id: v, level_id: "", course_id: "" }));
                setExamId(null); setQuestions([]);
              }}
            >
              <option value="">— اختر الدبلوم —</option>
              {diplomas.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </Field>

          <Field label="المستوى" required>
            <select
              value={form.level_id}
              onChange={(e) => {
                const v = e.target.value;
                setForm(f => ({ ...f, level_id: v, course_id: "" }));
                setExamId(null); setQuestions([]);
              }}
              disabled={!form.diploma_id}
            >
              <option value="">— اختر المستوى —</option>
              {levels.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </Field>

          <Field label="المقرر" required>
            <select
              value={form.course_id}
              onChange={(e) => {
                const v = e.target.value;
                setForm(f => ({ ...f, course_id: v }));
                setExamId(null); setQuestions([]);
              }}
              disabled={!form.level_id}
            >
              <option value="">— اختر المقرر —</option>
              {filteredCourses.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid g3">
          <Field label="عنوان الاختبار" required>
            <input
              type="text"
              placeholder="مثال: اختبار منتصف الفصل"
              value={form.title}
              onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
            />
          </Field>
          <Field label="الدرجة الكلية">
            <input
              type="number"
              min={1}
              step={1}
              value={form.total_grade}
              onChange={(e) => setForm(f => ({ ...f, total_grade: e.target.value }))}
            />
          </Field>
          <Field label="المدة (دقيقة)">
            <input
              type="number"
              min={10}
              step={5}
              value={form.duration_minutes}
              onChange={(e) => setForm(f => ({ ...f, duration_minutes: e.target.value }))}
            />
          </Field>
        </div>

        <div className="bar">
          <button
            className="btn primary"
            onClick={createExam}
            disabled={busy || !canCreate}
            title={examId ? "الامتحان موجود بالفعل" : ""}
          >
            {busy ? "جارٍ..." : (examId ? "الامتحان موجود" : "إنشاء الامتحان")}
          </button>
          {metaLoading && <span className="muted">تحميل البيانات…</span>}
          {metaErr && <span className="err">{metaErr}</span>}
        </div>
      </div>

      {/* إضافة/تعديل الأسئلة */}
      { (examId || (!examId && form.diploma_id && form.level_id && form.course_id)) && !examSaved && (
        <div className="card">
          <h2 className="card-title">{editingId ? "تعديل سؤال" : "إضافة سؤال"}</h2>
          <div className="grid g4">
            <Field label="نوع السؤال" required>
              <div className="seg">
                <button
                  className={`seg-btn ${qType === "TF" ? "active" : ""}`}
                  onClick={() => setQType("TF")}
                >صح/خطأ</button>
                <button
                  className={`seg-btn ${qType === "MCQ" ? "active" : ""}`}
                  onClick={() => setQType("MCQ")}
                >اختياري</button>
              </div>
            </Field>

            <div className="col-span-3">
              <Field label="نص السؤال" required>
                <textarea
                  rows={3}
                  placeholder="اكتب نص السؤال بوضوح…"
                  value={qText}
                  onChange={(e) => setQText(e.target.value)}
                />
              </Field>
            </div>
          </div>

          <div className="grid g3">
            <Field label="درجة السؤال">
              <input
                type="number"
                min={0}
                step={0.5}
                value={score}
                onChange={(e) => setScore(e.target.value)}
              />
            </Field>
          </div>

          {qType === "TF" ? (
            <div className="grid g3">
              <Field label="الإجابة الصحيحة" required>
                <div className="seg">
                  <button
                    className={`seg-btn ${tfCorrect === "صح" ? "active" : ""}`}
                    onClick={() => setTfCorrect("صح")}
                  >صح</button>
                  <button
                    className={`seg-btn ${tfCorrect === "خطأ" ? "active" : ""}`}
                    onClick={() => setTfCorrect("خطأ")}
                  >خطأ</button>
                </div>
              </Field>
            </div>
          ) : (
            <div className="mcq-box">
              <div className="mcq-list">
                {mcqOpts.map((o) => (
                  <div className="mcq-item" key={o.id}>
                    <button
                      type="button"
                      className={`flag ${o.correct ? "on" : ""}`}
                      title="تحديد كصحيح"
                      onClick={() => setCorrectOnly(o.id)}
                    >✓</button>
                    <input
                      type="text"
                      placeholder="نص الاختيار…"
                      value={o.text}
                      onChange={(e) => updateOpt(o.id, { text: e.target.value })}
                    />
                    <button
                      type="button"
                      className="icon danger"
                      title="حذف"
                      onClick={() => removeOpt(o.id)}
                      disabled={mcqOpts.length <= 2}
                    >✕</button>
                  </div>
                ))}
              </div>
              <div className="bar">
                <button type="button" className="btn" onClick={addOpt}>+ إضافة اختيار</button>
                <span className="muted">حد أدنى اختيارين | اختيار واحد صحيح</span>
              </div>
            </div>
          )}

          <div className="bar">
            <button className="btn primary" onClick={submitQuestion} disabled={busy}>
              {busy ? "جارٍ..." : (editingId ? "تحديث السؤال" : "إضافة السؤال")}
            </button>
            {editingId && (
              <button className="btn" onClick={() => {
                setEditingId(null); setQText(""); setScore(1); setQType("TF"); setTfCorrect("صح");
                setMcqOpts([{ id:1, text:"", correct:false }, { id:2, text:"", correct:false }]);
              }}>إلغاء التعديل</button>
            )}
            {toast && <span className="toast">{toast}</span>}
          </div>
        </div>
      )}

      {/* قائمة الأسئلة */}
      {examId && (
        <div className="card">
          <div className="questions-header">
            <h2 className="card-title">الأسئلة المضافة</h2>
            <div className="questions-actions">
              <div className="total-score">
                <Badge tone="green">الدرجة الكلية: {form.total_grade}</Badge>
              </div>
              {!examSaved && (
                <button className="btn success" onClick={saveExam} disabled={busy}>
                  {busy ? "جارٍ الحفظ..." : "حفظ الامتحان"}
                </button>
              )}
            </div>
          </div>

          <div className="exam-paper">
            <div className="exam-header">
              <div className="exam-title">{form.title || "اختبار"}</div>
              <div className="exam-meta">
                <span>المدة: {form.duration_minutes} دقيقة</span>
                <span>الدرجة الكلية: {form.total_grade}</span>
              </div>
            </div>

            {!examSaved && (
              <div className="order-controls">
                <h4>ترتيب الأسئلة:</h4>
                <div className="order-buttons">
                  <button className="btn outline small" onClick={() => reorderQuestions('tfFirst')}>صح/خطأ أولاً</button>
                  <button className="btn outline small" onClick={() => reorderQuestions('mcqFirst')}>اختياري أولاً</button>
                  <button className="btn outline small" onClick={() => reorderQuestions('shuffle')}>ترتيب عشوائي</button>
                </div>
              </div>
            )}

            <div className="question-section">
              {sectionOrder.map((t) => {
                const list = questions.filter(q => q.type === t);
                if (list.length === 0) return null;

                return (
                  <div key={t} className="question-group">
                    <h3 className="section-title">{t === "TF" ? "أسئلة الصح والخطأ" : "أسئلة الاختياري"}</h3>
                    <div className="questions-list">
                      {list.map((q, idx) => (
                        <QuestionItem
                          key={q.id}
                          question={q}
                          index={idx}
                          onEdit={startEdit}
                          onDelete={deleteQuestion}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* رسالة فارغة */}
      {(!examId && form.diploma_id && form.level_id && form.course_id && questions.length === 0) && (
        <div className="card">
          <div className="empty">
            <p>لا يوجد امتحان مسجل لهذا الاختيار حتى الآن.</p>
            <p className="muted">اكتب عنوان الامتحان وأنشئه لتبدأ بإضافة الأسئلة.</p>
          </div>
        </div>
      )}
    </div>
  );
}
